export class ObbyHUD {
  private container: HTMLDivElement;
  private timerDisplay: HTMLDivElement;
  private deathsDisplay: HTMLDivElement;
  private checkpointNotification: HTMLDivElement;
  private finishScreen: HTMLDivElement;
  private exitButton: HTMLButtonElement;
  private crosshair: HTMLDivElement;
  private clickToPlayMessage: HTMLDivElement;

  private onExitCallback: (() => void) | null = null;
  private onRestartCallback: (() => void) | null = null;

  constructor() {
    this.container = this.createContainer();
    this.timerDisplay = this.createTimerDisplay();
    this.deathsDisplay = this.createDeathsDisplay();
    this.checkpointNotification = this.createCheckpointNotification();
    this.finishScreen = this.createFinishScreen();
    this.exitButton = this.createExitButton();
    this.crosshair = this.createCrosshair();
    this.clickToPlayMessage = this.createClickToPlayMessage();

    this.container.appendChild(this.timerDisplay);
    this.container.appendChild(this.deathsDisplay);
    this.container.appendChild(this.checkpointNotification);
    this.container.appendChild(this.finishScreen);
    this.container.appendChild(this.exitButton);
    this.container.appendChild(this.crosshair);
    this.container.appendChild(this.clickToPlayMessage);

    document.body.appendChild(this.container);
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'obby-hud';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 200;
      font-family: 'Segoe UI', Arial, sans-serif;
      display: none;
    `;
    return container;
  }

  private createTimerDisplay(): HTMLDivElement {
    const timer = document.createElement('div');
    timer.id = 'obby-timer';
    timer.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 8px;
      color: white;
      font-size: 32px;
      font-weight: bold;
      font-family: 'Consolas', monospace;
    `;
    timer.textContent = '00:00.00';
    return timer;
  }

  private createDeathsDisplay(): HTMLDivElement {
    const deaths = document.createElement('div');
    deaths.id = 'obby-deaths';
    deaths.style.cssText = `
      position: absolute;
      top: 80px;
      right: 20px;
      padding: 8px 16px;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 8px;
      color: #ff6b6b;
      font-size: 18px;
    `;
    deaths.innerHTML = '&#x1F480; Mortes: 0';
    return deaths;
  }

  private createCheckpointNotification(): HTMLDivElement {
    const notification = document.createElement('div');
    notification.id = 'obby-checkpoint';
    notification.style.cssText = `
      position: absolute;
      top: 40%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 16px 32px;
      background: rgba(255, 200, 0, 0.9);
      border-radius: 8px;
      color: #333;
      font-size: 24px;
      font-weight: bold;
      display: none;
      animation: fadeInOut 2s ease-in-out;
    `;
    notification.textContent = 'Checkpoint!';

    // Add animation style
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        15% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        25% { transform: translate(-50%, -50%) scale(1); }
        75% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
    `;
    document.head.appendChild(style);

    return notification;
  }

  private createFinishScreen(): HTMLDivElement {
    const finish = document.createElement('div');
    finish.id = 'obby-finish';
    finish.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      text-align: center;
      color: white;
    `;

    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 48px;
      font-weight: bold;
      color: #4CAF50;
      margin-bottom: 20px;
    `;
    title.textContent = 'Parabens!';
    content.appendChild(title);

    const stats = document.createElement('div');
    stats.id = 'finish-stats';
    stats.style.cssText = `
      font-size: 24px;
      margin-bottom: 40px;
      line-height: 1.8;
    `;
    content.appendChild(stats);

    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      gap: 20px;
    `;

    const restartBtn = document.createElement('button');
    restartBtn.style.cssText = `
      padding: 16px 32px;
      font-size: 18px;
      font-weight: bold;
      color: white;
      background: #2196F3;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
    `;
    restartBtn.textContent = 'Jogar Novamente';
    restartBtn.addEventListener('click', () => {
      if (this.onRestartCallback) {
        this.onRestartCallback();
      }
    });
    restartBtn.addEventListener('mouseenter', () => {
      restartBtn.style.transform = 'scale(1.05)';
      restartBtn.style.filter = 'brightness(1.2)';
    });
    restartBtn.addEventListener('mouseleave', () => {
      restartBtn.style.transform = 'scale(1)';
      restartBtn.style.filter = 'brightness(1)';
    });
    buttonsContainer.appendChild(restartBtn);

    const lobbyBtn = document.createElement('button');
    lobbyBtn.style.cssText = `
      padding: 16px 32px;
      font-size: 18px;
      font-weight: bold;
      color: white;
      background: #666;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
    `;
    lobbyBtn.textContent = 'Voltar ao Lobby';
    lobbyBtn.addEventListener('click', () => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });
    lobbyBtn.addEventListener('mouseenter', () => {
      lobbyBtn.style.transform = 'scale(1.05)';
      lobbyBtn.style.filter = 'brightness(1.2)';
    });
    lobbyBtn.addEventListener('mouseleave', () => {
      lobbyBtn.style.transform = 'scale(1)';
      lobbyBtn.style.filter = 'brightness(1)';
    });
    buttonsContainer.appendChild(lobbyBtn);

    content.appendChild(buttonsContainer);
    finish.appendChild(content);

    return finish;
  }

  private createExitButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = 'obby-exit';
    button.style.cssText = `
      position: absolute;
      top: 20px;
      left: 20px;
      padding: 10px 20px;
      font-size: 14px;
      font-weight: bold;
      color: white;
      background: rgba(100, 100, 100, 0.8);
      border: none;
      border-radius: 6px;
      cursor: pointer;
      pointer-events: auto;
      transition: all 0.2s;
    `;
    button.textContent = 'Sair';
    button.addEventListener('click', () => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });
    button.addEventListener('mouseenter', () => {
      button.style.background = 'rgba(150, 150, 150, 0.9)';
    });
    button.addEventListener('mouseleave', () => {
      button.style.background = 'rgba(100, 100, 100, 0.8)';
    });
    return button;
  }

  private createCrosshair(): HTMLDivElement {
    const crosshair = document.createElement('div');
    crosshair.id = 'obby-crosshair';
    crosshair.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      display: none;
    `;

    // Horizontal line
    const hLine = document.createElement('div');
    hLine.style.cssText = `
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background: white;
      transform: translateY(-50%);
      box-shadow: 0 0 2px rgba(0,0,0,0.8);
    `;

    // Vertical line
    const vLine = document.createElement('div');
    vLine.style.cssText = `
      position: absolute;
      top: 0;
      left: 50%;
      width: 2px;
      height: 100%;
      background: white;
      transform: translateX(-50%);
      box-shadow: 0 0 2px rgba(0,0,0,0.8);
    `;

    crosshair.appendChild(hLine);
    crosshair.appendChild(vLine);
    return crosshair;
  }

  private createClickToPlayMessage(): HTMLDivElement {
    const message = document.createElement('div');
    message.id = 'obby-click-to-play';
    message.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px 40px;
      background: rgba(0, 0, 0, 0.8);
      border-radius: 10px;
      color: white;
      font-size: 18px;
      text-align: center;
      pointer-events: none;
    `;
    message.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 10px;">Controles</div>
      <div style="font-size: 14px; color: #aaa;">
        Botão direito + Mouse: Olhar ao redor<br>
        WASD: Mover | Espaço: Pular
      </div>
    `;
    // Hide by default - no click to play needed
    message.style.display = 'none';
    return message;
  }

  public setPointerLocked(locked: boolean): void {
    // Now "locked" means right mouse button is held (camera rotation mode)
    if (locked) {
      this.crosshair.style.display = 'block';
    } else {
      this.crosshair.style.display = 'none';
    }
  }

  public setTime(seconds: number): void {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    this.timerDisplay.textContent =
      `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }

  public setDeaths(count: number): void {
    this.deathsDisplay.innerHTML = `&#x1F480; Mortes: ${count}`;
  }

  public showCheckpoint(): void {
    this.checkpointNotification.style.display = 'block';
    this.checkpointNotification.style.animation = 'none';
    // Trigger reflow
    void this.checkpointNotification.offsetWidth;
    this.checkpointNotification.style.animation = 'fadeInOut 2s ease-in-out';

    setTimeout(() => {
      this.checkpointNotification.style.display = 'none';
    }, 2000);
  }

  public showFinish(time: number, deaths: number): void {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 100);
    const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;

    const stats = this.finishScreen.querySelector('#finish-stats') as HTMLDivElement;
    stats.innerHTML = `
      <div>Tempo: <span style="color: #4CAF50; font-weight: bold;">${timeStr}</span></div>
      <div>Mortes: <span style="color: #ff6b6b; font-weight: bold;">${deaths}</span></div>
    `;

    this.finishScreen.style.display = 'flex';
  }

  public hideFinish(): void {
    this.finishScreen.style.display = 'none';
  }

  public onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  public onRestart(callback: () => void): void {
    this.onRestartCallback = callback;
  }

  public show(): void {
    this.container.style.display = 'block';
  }

  public hide(): void {
    this.container.style.display = 'none';
  }

  public dispose(): void {
    this.container.remove();
  }
}
